import PropTypes from "prop-types"
import React, { useRef, useState } from "react"

import Button from "src/components/Button"

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const fileExtension = (name) => name?.split(".").pop()?.toUpperCase()

const fileKindLabel = (name) => {
  const extension = fileExtension(name)
  if (["PDF", "JPG", "PNG"].includes(extension)) return extension
  return extension || "FILE"
}

const fileKindIcon = (name) => {
  const extension = fileExtension(name)?.toLowerCase()

  if (extension === "pdf") return { icon: "fa-file-pdf", color: "text-red-500" }
  if (extension === "png" || extension === "jpg" || extension === "jpeg")
    return { icon: "fa-file-image", color: "text-blue-500" }

  return { icon: "fa-file-lines", color: "text-gray-500" }
}

const FileUpload = ({
  label,
  accept,
  copy,
  onValidate,
  onAccepted,
  onRejected,
  existingDocument,
  value,
  error,
  replacing = false,
  onReplace,
  onCancelReplace,
}) => {
  const fileInputRef = useRef(null)

  const [isValidating, setIsValidating] = useState(false)
  const [pendingName, setPendingName] = useState(null)

  const handleFiles = async (files) => {
    const file = files?.[0]
    if (!file) return

    setPendingName(file.name)
    setIsValidating(true)

    let result
    try {
      result = await onValidate(file)
    } catch {
      setIsValidating(false)
      onRejected?.(copy.failure)
      return
    }

    setIsValidating(false)

    if (result?.valid) {
      onAccepted?.(file, result)
    } else {
      onRejected?.(result?.error)
    }
  }

  const handleInputChange = (event) => {
    handleFiles(event.target.files)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    handleFiles(event.dataTransfer.files)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const handleReplace = () => {
    onReplace?.()
  }

  const renderHiddenInput = () => (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      onChange={handleInputChange}
      className="hidden"
      aria-label={label}
    />
  )

  const renderDropZone = () => (
    <div>
      <div
        className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <i
          className="fa-solid fa-arrow-up-from-bracket text-[48px] text-blue-700"
          aria-hidden="true"
        />
        <p className="mb-1 mt-2 font-semibold text-gray-700">{label}</p>
        <p className="mb-4 text-gray-600">{copy.hint}</p>
        <div className="flex items-center justify-center space-x-2">
          <Button variant="secondary" onClick={handleBrowseClick}>
            {copy.browseFiles}
          </Button>
          {replacing && existingDocument && (
            <Button variant="secondary" onClick={onCancelReplace}>
              {copy.keepOnFile}
            </Button>
          )}
        </div>
      </div>
      {error && <p className="mt-2 text-red-600">{error}</p>}
    </div>
  )

  const renderValidating = () => (
    <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center">
      <i
        className="fa-solid fa-spinner-third fa-spin text-blue-700"
        aria-hidden="true"
      />
      <p className="mt-2 font-semibold text-gray-700">
        {copy.validating.replace("{{name}}", pendingName)}
      </p>
    </div>
  )

  const renderSuccess = () => {
    const { icon, color } = fileKindIcon(value.name)

    return (
      <div className="flex items-center justify-between rounded-lg border border-gray-300 bg-white p-4 shadow-md">
        <div className="flex items-center space-x-3">
          <i
            className={`fa-solid ${icon} text-2xl ${color}`}
            aria-hidden="true"
          />
          <div>
            <p className="font-semibold text-gray-900">{value.name}</p>
            <p className="text-sm text-gray-600">
              {fileKindLabel(value.name)} &middot; {formatFileSize(value.size)}{" "}
              <i
                className="fa-solid fa-check text-teal-600"
                aria-hidden="true"
              />{" "}
              <span className="text-teal-600">{copy.uploaded}</span>
            </p>
          </div>
        </div>
        <Button variant="secondary" onClick={handleReplace}>
          {copy.replace}
        </Button>
      </div>
    )
  }

  const renderAttached = () => {
    const { icon, color } = fileKindIcon(existingDocument.name)

    return (
      <div className="flex items-center justify-between rounded-lg border border-gray-300 bg-white p-4 shadow-md">
        <div className="flex items-center space-x-3">
          <i
            className={`fa-solid ${icon} text-2xl ${color}`}
            aria-hidden="true"
          />
          <div>
            <p className="font-semibold text-gray-900">
              {existingDocument.name || copy.documentOnFile}
            </p>
            <p className="text-sm text-teal-600">
              <i className="fa-solid fa-check" aria-hidden="true" />{" "}
              {copy.attached}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <a
            href={existingDocument.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            {copy.view}
          </a>
          <Button variant="secondary" onClick={handleReplace}>
            {copy.replace}
          </Button>
        </div>
      </div>
    )
  }

  const renderBody = () => {
    if (isValidating) return renderValidating()
    if (error) return renderDropZone()
    if (replacing) return renderDropZone()
    if (value) return renderSuccess()
    if (existingDocument) return renderAttached()
    return renderDropZone()
  }

  return (
    <div>
      {renderHiddenInput()}
      {renderBody()}
    </div>
  )
}

FileUpload.propTypes = {
  label: PropTypes.string.isRequired,
  accept: PropTypes.string.isRequired,
  copy: PropTypes.shape({
    browseFiles: PropTypes.string.isRequired,
    replace: PropTypes.string.isRequired,
    view: PropTypes.string.isRequired,
    documentOnFile: PropTypes.string.isRequired,
    uploaded: PropTypes.string.isRequired,
    attached: PropTypes.string.isRequired,
    hint: PropTypes.string.isRequired,
    validating: PropTypes.string.isRequired,
    failure: PropTypes.string.isRequired,
    keepOnFile: PropTypes.string.isRequired,
  }).isRequired,
  onValidate: PropTypes.func.isRequired,
  onAccepted: PropTypes.func,
  onRejected: PropTypes.func,
  existingDocument: PropTypes.shape({
    url: PropTypes.string,
    name: PropTypes.string,
  }),
  value: PropTypes.object,
  error: PropTypes.string,
  replacing: PropTypes.bool,
  onReplace: PropTypes.func,
  onCancelReplace: PropTypes.func,
}

export default FileUpload
